import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { FormControlLabel, Radio, RadioGroup, Input } from '@material-ui/core';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  changeTrip,
  changeValidation,
  addCustomBookingFieldRequest,
  resetAddCustomBookingField,
} from '../../../store/slices/trip';
import TwExpansionPanel from '../../../HOCs/TwExpansionPanel';
import useStyles from '../CruiseConfig/styles';
import ErrorTooltip from '../../ErrorTooltip';
import BookingItemTitle from '../BookingItemTitle';
import {
  CheckoutFieldPlacement,
  checkoutCustomBookingsFieldTypes,
  SearchStatus,
  UserRolePermissions,
} from '../../../constants';
import SearchAutocomplete from '../../SearchAutocomplete';
import Button from '../../Button';
import { checkSitePermission } from '../../../store/slices/user';

export const shouldShowField = (field, selectedFields) => {
  if (!field.showWhen) return true;

  return field.showWhen.split(',').some((condition) => {
    const [conditionKey, expectedValue] = condition.split('=');
    return selectedFields[conditionKey] === expectedValue;
  });
};

const getSelectedOption = (options, selectedValue) =>
  options?.find((option) => option.value === selectedValue) || '';

const parseCustomBookingFields = (json) => {
  try {
    return JSON.parse(json || '{}');
  } catch {
    return {};
  }
};

const AdditionalCheckoutFields = ({ doDisableTravelerInputs, fieldPlacement }) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { selectedAdditionalCheckoutFields = {}, tripConfirmation } = useSelector(
    (store) => store.trip,
    shallowEqual,
  );
  const { invalidAdditionalCheckoutFields = {} } = useSelector((store) => store.trip);
  const { travelAgency = {}, isLoggedIn } = useSelector((store) => store.user);
  const canChangeBooking = useSelector(checkSitePermission(UserRolePermissions.canChangeBooking));
  const { addCustomBookingFieldStatus, addCustomBookingFieldError } = useSelector(
    (store) => store.trip,
  );

  const [activeSaveFieldKey, setActiveSaveFieldKey] = useState(null);
  const [postBookingSaveSuccessFieldKey, setPostBookingSaveSuccessFieldKey] = useState(null);

  const checkoutFields = useMemo(
    () =>
      travelAgency.checkoutFields?.length > 0
        ? travelAgency.checkoutFields.filter(
            (f) =>
              (!fieldPlacement || f.showWhere === fieldPlacement) &&
              !(f.hideForAnonymous && !isLoggedIn),
          )
        : [],
    [fieldPlacement, isLoggedIn, travelAgency.checkoutFields],
  );

  const customBookingFields = useMemo(
    () => parseCustomBookingFields(tripConfirmation?.customBookingFields),
    [tripConfirmation?.customBookingFields],
  );

  const pendingPostBookingFieldKeys = useMemo(() => {
    if (!tripConfirmation || !isLoggedIn || !canChangeBooking) return new Set();
    const alreadySetKeys = new Set(Object.keys(customBookingFields));
    return new Set(
      checkoutFields
        .filter(
          (f) =>
            f.hideForAnonymous &&
            f.fieldTypes?.includes(checkoutCustomBookingsFieldTypes.remark) &&
            !alreadySetKeys.has(f.key),
        )
        .map((f) => f.key),
    );
  }, [customBookingFields, tripConfirmation, isLoggedIn, canChangeBooking, checkoutFields]);

  useEffect(() => {
    if (
      activeSaveFieldKey &&
      addCustomBookingFieldStatus === SearchStatus.DONE &&
      !addCustomBookingFieldError
    ) {
      setPostBookingSaveSuccessFieldKey(activeSaveFieldKey);
      setActiveSaveFieldKey(null);
      dispatch(resetAddCustomBookingField());
    }
  }, [activeSaveFieldKey, addCustomBookingFieldStatus, addCustomBookingFieldError, dispatch]);

  useEffect(() => {
    if (!postBookingSaveSuccessFieldKey) return undefined;
    const timer = setTimeout(() => setPostBookingSaveSuccessFieldKey(null), 5000);
    return () => clearTimeout(timer);
  }, [postBookingSaveSuccessFieldKey]);

  useEffect(
    () => () => {
      dispatch(resetAddCustomBookingField());
    },
    [dispatch],
  );

  useEffect(() => {
    checkoutFields.forEach((field) => {
      const value = selectedAdditionalCheckoutFields[field.key];
      if (
        field.inputType === 'Radio' &&
        shouldShowField(field, selectedAdditionalCheckoutFields) &&
        (value === undefined || value === '') &&
        field.defaultValue
      ) {
        dispatch(
          changeTrip({
            field: 'selectedAdditionalCheckoutFields',
            value: { ...selectedAdditionalCheckoutFields, [field.key]: field.defaultValue },
          }),
        );
      }
    });
    if (Object.keys(customBookingFields).length) {
      Object.entries(customBookingFields).forEach(([key, value]) => {
        if (!selectedAdditionalCheckoutFields?.[key]) {
          dispatch(
            changeTrip({
              field: 'selectedAdditionalCheckoutFields',
              value: {
                ...selectedAdditionalCheckoutFields,
                [key]: value,
              },
            }),
          );
        }
      });
    }
  }, [checkoutFields, selectedAdditionalCheckoutFields, dispatch, customBookingFields]);

  const getInputValue = useCallback(
    (field) =>
      customBookingFields?.[field?.key]?.toString() ?? selectedAdditionalCheckoutFields[field.key],
    [selectedAdditionalCheckoutFields, customBookingFields],
  );

  const validateAndDispatchCheckoutFields = useCallback(
    (checkoutFieldName, value) => {
      const updatedValues = { ...selectedAdditionalCheckoutFields, [checkoutFieldName]: value };

      if (value === null || value === '') {
        delete updatedValues[checkoutFieldName];
      }

      checkoutFields.forEach((field) => {
        if (!shouldShowField(field, updatedValues)) {
          delete updatedValues[field.key];
        }
      });

      dispatch(changeTrip({ field: 'selectedAdditionalCheckoutFields', value: updatedValues }));
      const customDKField = checkoutFields.find((f) =>
        f.fieldTypes?.includes(checkoutCustomBookingsFieldTypes.customDKInput),
      );

      if (customDKField && checkoutFieldName === customDKField.key) {
        dispatch(
          changeTrip({
            field: 'customerIdentifier',
            value: value || '',
          }),
        );
      }
      return updatedValues;
    },
    [checkoutFields, dispatch, selectedAdditionalCheckoutFields],
  );

  const renderField = useCallback(
    (field, isReadOnly) => {
      switch (field.inputType) {
        case 'Radio':
          return (
            <RadioGroup
              onChange={(event) =>
                validateAndDispatchCheckoutFields(field.key, event?.target?.value, field)
              }
              row
              value={getInputValue(field)?.toString() ?? field.defaultValue ?? ''}
            >
              {Object.keys(field.options || {}).map((key) => (
                <FormControlLabel
                  value={field.options[key]}
                  control={<Radio />}
                  label={key}
                  disabled={isReadOnly}
                  key={key}
                />
              ))}
            </RadioGroup>
          );
        case 'Text':
          return (
            <div>
              <Input
                disabled={isReadOnly}
                type="text"
                placeholder={field.placeholder?.length ? field.placeholder : field.fieldName}
                inputProps={{ maxLength: field.maxLength }}
                className="px-6 py-3 min-h-12 rounded-2xl bg-gray-500 bg-opacity-5 placeholder-gray-500F w-full"
                classes={classes}
                disableUnderline
                value={
                  getInputValue(field)?.toString() ??
                  selectedAdditionalCheckoutFields[field.key] ??
                  field?.defaultValue ??
                  ''
                }
                onChange={(e) =>
                  validateAndDispatchCheckoutFields(field.key, e?.target?.value, field)
                }
              />
            </div>
          );
        default: {
          const options = Object.keys(field.options || {}).map((key) => ({
            value: field.options[key],
            label: key,
          }));
          return options && options.length ? (
            <div>
              <SearchAutocomplete
                disabled={isReadOnly}
                placeholderText={field.fieldName + (field.isRequiredField ? ' (required)' : '')}
                options={[{ suggests: options }]}
                values={getSelectedOption(
                  options,
                  getInputValue(field)?.toString() ?? selectedAdditionalCheckoutFields[field.key],
                )}
                onChange={(selectedValue) =>
                  validateAndDispatchCheckoutFields(field.key, selectedValue?.value, field)
                }
                autoFocus={false}
                freeSolo={false}
              />
            </div>
          ) : (
            <></>
          );
        }
      }
    },
    [classes, validateAndDispatchCheckoutFields, selectedAdditionalCheckoutFields, getInputValue],
  );

  const handlePostBookingSave = useCallback(
    (field) => {
      const value = (getInputValue(field) ?? selectedAdditionalCheckoutFields[field.key] ?? '')
        .toString()
        .trim();
      if (!value) return;
      const tripId = tripConfirmation?.tripId;
      if (!tripId) return;
      setActiveSaveFieldKey(field.key);
      dispatch(addCustomBookingFieldRequest({ tripId, fieldKey: field.key, fieldValue: value }));
    },
    [dispatch, getInputValue, selectedAdditionalCheckoutFields, tripConfirmation],
  );

  const handleFormValidationsReset = useCallback(() => {
    dispatch(
      changeValidation({
        invalidAdditionalCheckoutFields: {},
      }),
    );
  }, [dispatch]);

  const renderFields = useMemo(() => {
    if (!checkoutFields || !checkoutFields.length) return <></>;

    const isSubmitting = addCustomBookingFieldStatus === SearchStatus.SEARCHING;

    return (
      <div
        className={clsx({
          'grid grid-cols-2 gap-4': fieldPlacement === CheckoutFieldPlacement.PrimaryPassenger,
          'mb-4 m-4 sm:mx-6': fieldPlacement !== CheckoutFieldPlacement.PrimaryPassenger,
        })}
        onFocus={handleFormValidationsReset}
      >
        {checkoutFields
          .filter(
            (f) =>
              shouldShowField(f, selectedAdditionalCheckoutFields) || f.key in customBookingFields,
          )
          .map((field) => {
            const canSavePostBooking = pendingPostBookingFieldKeys.has(field.key);
            const isActiveSaveField = activeSaveFieldKey === field.key && isSubmitting;
            const isFieldReadOnly = canSavePostBooking
              ? isActiveSaveField
              : doDisableTravelerInputs;
            const currentValue = (
              getInputValue(field) ??
              selectedAdditionalCheckoutFields[field.key] ??
              ''
            )
              .toString()
              .trim();

            return (
              <div key={field.key} className="flex flex-col mb-4">
                <div className="flex items-center">
                  {fieldPlacement !== CheckoutFieldPlacement.PrimaryPassenger && (
                    <div className="w-full sm:w-4/5 pr-2 mb-2 sm:mb-0">
                      {field.fieldName}
                      {field.isRequiredField && ' (required)'}
                      {field.helperLabel && (
                        <div className="text-gray-600 text-sm">{field.helperLabel}</div>
                      )}
                    </div>
                  )}
                  <div className={clsx('w-full', canSavePostBooking && 'flex items-stretch gap-2')}>
                    <div className={clsx(canSavePostBooking && 'flex-1 min-w-0')}>
                      <ErrorTooltip
                        title={
                          field.fieldTypes?.includes(
                            checkoutCustomBookingsFieldTypes.customDKInput,
                          ) && field.maxLength
                            ? `Customer identifier must be ${field.maxLength} alphanumeric characters`
                            : `Please provide valid ${field.fieldName}`
                        }
                        open={
                          !!(
                            invalidAdditionalCheckoutFields &&
                            invalidAdditionalCheckoutFields[field.key]
                          )
                        }
                        placement="bottom"
                        arrow
                        PopperProps={{ disablePortal: true }}
                      >
                        {renderField(field, isFieldReadOnly)}
                      </ErrorTooltip>
                    </div>
                    {canSavePostBooking && (
                      <Button
                        variant={Button.buttonVariants.PRIMARY}
                        size={Button.buttonSizes.SM}
                        className="min-h-12 px-6 w-auto whitespace-nowrap self-stretch"
                        onClick={() => handlePostBookingSave(field)}
                        doShowLoadingState={isActiveSaveField}
                        disabled={isActiveSaveField || !currentValue}
                      >
                        <span className="text-base">Save</span>
                      </Button>
                    )}
                  </div>
                </div>
                {canSavePostBooking &&
                  activeSaveFieldKey === field.key &&
                  addCustomBookingFieldError && (
                    <div className="text-red-600 text-sm mt-1">{addCustomBookingFieldError}</div>
                  )}
                {postBookingSaveSuccessFieldKey === field.key && (
                  <div className="text-green-700 text-sm mt-1 flex items-center gap-1">
                    <DoneAllIcon fontSize="small" className="text-green-500" />
                    {field.fieldName} saved successfully.
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  }, [
    checkoutFields,
    fieldPlacement,
    getInputValue,
    handleFormValidationsReset,
    invalidAdditionalCheckoutFields,
    renderField,
    selectedAdditionalCheckoutFields,
    customBookingFields,
    handlePostBookingSave,
    pendingPostBookingFieldKeys,
    activeSaveFieldKey,
    postBookingSaveSuccessFieldKey,
    doDisableTravelerInputs,
    addCustomBookingFieldError,
    addCustomBookingFieldStatus,
  ]);

  if (!checkoutFields || !checkoutFields.length) return <></>;

  if (fieldPlacement === CheckoutFieldPlacement.TravelAgencyDetails) {
    return <>{renderFields}</>;
  }

  return fieldPlacement === CheckoutFieldPlacement.PrimaryPassenger ? (
    renderFields
  ) : (
    <TwExpansionPanel>
      <div className="text-grey-700 mb-5" id="additional-checkout-fields">
        <BookingItemTitle placeholder="Additional Details" />
        {renderFields}
      </div>
    </TwExpansionPanel>
  );
};

AdditionalCheckoutFields.propTypes = {
  doDisableTravelerInputs: PropTypes.bool,
  fieldPlacement: PropTypes.string,
};

AdditionalCheckoutFields.defaultProps = {
  doDisableTravelerInputs: false,
  fieldPlacement: undefined,
};

export default AdditionalCheckoutFields;
